import { createApp } from 'vue';
import useTippyPopup from '@/js/composables/useTippyPopup';
import TopExchangesTippy from '@/js/components/Tippy/TopExchangesTippy/TopExchangesTippy.vue';

interface VueMountedElement extends HTMLElement {
    vueApp?: ReturnType<typeof createApp>;
}

type TippyInstanceList = ReturnType<typeof useTippyPopup>[];

let tippyInstances: TippyInstanceList = [];

/**
 * Mount all <top-exchanges-tippy> components by locating their parent wrapper elements.
 * Handles Vue app mounting and cleanup.
 */
function mountVueTippies(): void {
    document.querySelectorAll('top-exchanges-tippy').forEach((el) => {
        const wrapper = el.parentElement as VueMountedElement;
        if (!wrapper) return;

        if (wrapper.vueApp) {
            wrapper.vueApp.unmount();
            wrapper.vueApp = null;
        }

        const app = createApp({});
        app.component('TopExchangesTippy', TopExchangesTippy);
        app.mount(wrapper);
        wrapper.vueApp = app;
    });
}

/**
 * Initialize all Tippy.js-based tooltips with content templates.
 */
function initializeTippyTooltips(): void {
    tippyInstances.forEach((instance) => instance.destroy());

    const tableElements = document.querySelectorAll<HTMLElement>('.tippy-table');
    tippyInstances = [];

    tableElements.forEach((element) => {
        const templateId = element.getAttribute('data-template');
        if (!templateId) {
            return;
        }

        const instance = useTippyPopup(element, {
            isContentReactive: true,
            content(reference) {
                const id = reference.getAttribute('data-template');
                if (!id) {
                    return null;
                }
                const template = document.getElementById(id);

                return template || null;
            },
            placement: 'bottom',
            interactive: true,
            appendTo: () => document.querySelector('.tooltip-append') || document.body,
        });
        tippyInstances.push(instance);
    });
}

/**
 * Fully (re)initialize all tooltips: Vue and Tippy.js.
 */
function reinitializeAllTooltips(): void {
    requestAnimationFrame(() => {
        initializeTippyTooltips();
        mountVueTippies();
    });
}

/**
 * Set up Livewire integration to reinitialize tooltips on DOM changes.
 */
export function setupLivewireTooltips(): void {
    reinitializeAllTooltips();

    if (typeof window.Livewire !== 'undefined') {
        window.Livewire.on('tableChanged', () => {
            reinitializeAllTooltips();
        });
    }
}

setupLivewireTooltips();
